import { supabase } from '../supabaseClient';

export const conferencesService = {
  async getConferencesData(editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get conference rooms
      const { data: salles } = await supabase
        .from('conferences_salles')
        .select('*')
        .eq('edition_id', edition.id)
        .order('nom_salle');

      // Get conferences
      const { data: conferences } = await supabase
        .from('conferences')
        .select(`
          *,
          conferences_salles!salle_id (nom_salle)
        `)
        .eq('edition_id', edition.id)
        .order('date_debut, heure_debut');

      // Get conference-speaker links
      const { data: confSpeakers } = await supabase
        .from('conferences_speakers')
        .select('*');

      return {
        conferences: (conferences || []).map(conference => ({
          id: conference.id,
          titre: conference.titre,
          dateDebut: conference.date_debut,
          heureDebut: conference.heure_debut,
          dateFin: conference.date_fin,
          heureFin: conference.heure_fin,
          salle: conference.conferences_salles?.nom_salle || '',
          salleId: conference.salle_id,
          intervenant: conference.intervenant,
          description: conference.description,
          statut: conference.statut,
          speakers: (confSpeakers || [])
            .filter(cs => cs.conference_id === conference.id)
            .map(cs => String(cs.speaker_id))
        })),
        salles: (salles || []).map(salle => ({
          id: salle.id,
          nom: salle.nom_salle,
          capacite: salle.capacite,
          equipements: salle.equipements
        })),
        statutOptions: [
          { value: 'en_attente', label: 'En Attente', color: '#ffc107' },
          { value: 'confirmee', label: 'Confirmée', color: '#28a745' },
          { value: 'annulee', label: 'Annulée', color: '#dc3545' }
        ]
      };
    } catch (error) {
      throw error;
    }
  },

  async addSalle(salleData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const insertData = {
        edition_id: edition.id,
        nom_salle: salleData.nom,
        capacite: salleData.capacite,
        equipements: salleData.equipements
      };

      const { data, error } = await supabase
        .from('conferences_salles')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateSalle(salleId, salleData) {
    try {
      const updateData = {
        nom_salle: salleData.nom,
        capacite: salleData.capacite,
        equipements: salleData.equipements,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('conferences_salles')
        .update(updateData)
        .eq('id', salleId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteSalle(salleId) {
    try {
      // Check if salle is used by any conferences
      const { data: conferences } = await supabase
        .from('conferences')
        .select('id')
        .eq('salle_id', salleId)
        .limit(1);

      if (conferences && conferences.length > 0) {
        throw new Error('Cette salle est utilisée par des conférences et ne peut pas être supprimée.');
      }

      const { error } = await supabase
        .from('conferences_salles')
        .delete()
        .eq('id', salleId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async addConference(conferenceData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const insertData = {
        edition_id: edition.id,
        titre: conferenceData.titre,
        date_debut: conferenceData.dateDebut,
        heure_debut: conferenceData.heureDebut,
        date_fin: conferenceData.dateFin,
        heure_fin: conferenceData.heureFin,
        salle_id: conferenceData.salleId,
        intervenant: conferenceData.intervenant,
        description: conferenceData.description,
        statut: conferenceData.statut || 'en_attente'
      };

      const { data, error } = await supabase
        .from('conferences')
        .insert([insertData])
        .select(`
          *,
          conferences_salles!salle_id (nom_salle)
        `)
        .single();

      if (error) throw error;

      // Link speakers if provided
      if (conferenceData.speakers && conferenceData.speakers.length > 0) {
        const links = conferenceData.speakers.map(speakerId => ({
          conference_id: data.id,
          speaker_id: parseInt(speakerId)
        }));
        await supabase.from('conferences_speakers').insert(links);
      }

      // Attach speakers array for UI
      return { ...data, speakers: conferenceData.speakers || [] };
    } catch (error) {
      throw error;
    }
  },

  async updateConference(conferenceId, conferenceData) {
    try {
      const updateData = {
        titre: conferenceData.titre,
        date_debut: conferenceData.dateDebut,
        heure_debut: conferenceData.heureDebut,
        date_fin: conferenceData.dateFin,
        heure_fin: conferenceData.heureFin,
        salle_id: conferenceData.salleId,
        intervenant: conferenceData.intervenant,
        description: conferenceData.description,
        statut: conferenceData.statut || 'en_attente',
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('conferences')
        .update(updateData)
        .eq('id', conferenceId)
        .select(`
          *,
          conferences_salles!salle_id (nom_salle)
        `)
        .single();

      if (error) throw error;

      // Update speakers links
      if (conferenceData.speakers) {
        // Remove old links
        await supabase
          .from('conferences_speakers')
          .delete()
          .eq('conference_id', conferenceId);

        // Add new links
        if (conferenceData.speakers.length > 0) {
          const links = conferenceData.speakers.map(speakerId => ({
            conference_id: conferenceId,
            speaker_id: parseInt(speakerId)
          }));
          await supabase.from('conferences_speakers').insert(links);
        }
      }

      // Attach speakers array for UI
      return { ...data, speakers: conferenceData.speakers || [] };
    } catch (error) {
      throw error;
    }
  },

  async deleteConference(conferenceId) {
    try {
      // Check if conference has visitors registered
      const { data: visiteurs } = await supabase
        .from('visiteurs')
        .select('id')
        .eq('conference_id', conferenceId)
        .limit(1);

      if (visiteurs && visiteurs.length > 0) {
        throw new Error('Cette conférence a des visiteurs inscrits et ne peut pas être supprimée.');
      }

      const { error } = await supabase
        .from('conferences')
        .delete()
        .eq('id', conferenceId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }
};
